/**
 * EplPublicLabs - Página pública de laboratorios
 * 
 * Vista para estudiantes que muestra laboratorios activos
 * filtrados por systemCard (innovafast, innovatech, transforma360)
 * 
 * Características:
 * - Solo muestra labs con status='active'
 * - Filtrado por systemCard desde URL params
 * - Búsqueda por título/descripción
 * - Filtros por categoría y dificultad
 * - Cards con información básica
 * - Acceso directo a ejecutar laboratorio
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEplLabs } from '../hooks/useEpl';
import { useAuth } from '../../context/AuthContext';
import { getSafeToken } from '../../utils/tokenHelper';
import { 
  Search, 
  Filter, 
  BookOpen, 
  Clock, 
  Target, 
  Tag, 
  CheckCircle2, 
  Award, 
  GraduationCap,
  MessageSquare,
  Users,
  Lightbulb,
  TrendingUp,
  BarChart,
  Settings,
  Shield,
  Map
} from 'lucide-react';
import axios from 'axios';
import EplStatusBadge from '../components/EplStatusBadge';

// 🔧 Helper para obtener API URL según entorno
const getApiUrl = () => {
  // Prioridad 1: Variable de entorno explícita
  if (import.meta.env.VITE_EPL_API_URL) {
    return import.meta.env.VITE_EPL_API_URL;
  }
  
  // Prioridad 2: Detectar automáticamente según hostname
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  
  // Producción: usar el mismo origin (ya está en el servidor)
  return window.location.origin;
};

const API_URL = getApiUrl();

// Mapeo de systemCard a nombres legibles (9 tarjetas EPL)
const SYSTEM_NAMES = {
  // EPL-Leader Lab
  'gestion_crisis': 'Gestión de Crisis',
  'dinamicas_equipo': 'Dinámicas de Equipo',
  'comunicacion_estrategica': 'Comunicación Estratégica',
  // EPL-Innova System
  'innovafast': 'InnovaFast',
  'innovatech': 'InnovaTech',
  'transforma360': 'Transforma360',
  // EPL-Decision System
  'dss_builder': 'DSS Builder',
  'analisis_riesgo': 'Análisis de Riesgo',
  'planificacion_estrategica': 'Planificación Estratégica'
};

// Mapeo de sistemas EPL a tarjetas permitidas
const EPL_SYSTEM_MAPPING = {
  'EPL-Leader Lab': ['gestion_crisis', 'dinamicas_equipo', 'comunicacion_estrategica'],
  'EPL-Innova System': ['innovafast', 'innovatech', 'transforma360'],
  'EPL-Decision System': ['dss_builder', 'analisis_riesgo', 'planificacion_estrategica']
};

// Configuración de mentores por systemCard
const MENTORS_CONFIG = {
  'gestion_crisis': {
    name: 'Dra. Laura Crisis',
    title: 'Mentora de Liderazgo',
    quote: 'En la crisis está la oportunidad de liderar con claridad.',
    specialty: 'Experta en gestión de equipos bajo presión',
    avatarIcon: MessageSquare, // Ícono SVG temporal
    color: 'from-purple-50 to-purple-100',
    borderColor: 'border-purple-300',
    iconColor: 'text-purple-600'
  },
  'dinamicas_equipo': {
    name: 'Prof. Manuel Synergy',
    title: 'Mentor de Dinámicas',
    quote: 'Los equipos efectivos transforman organizaciones.',
    specialty: 'Especialista en cohesión y resolución de conflictos',
    avatarIcon: Users,
    color: 'from-purple-50 to-purple-100',
    borderColor: 'border-purple-300',
    iconColor: 'text-purple-600'
  },
  'comunicacion_estrategica': {
    name: 'Lic. Ana Comunicar',
    title: 'Mentora de Comunicación',
    quote: 'Comunicar con impacto es liderar con influencia.',
    specialty: 'Experta en oratoria ejecutiva',
    avatarIcon: Target,
    color: 'from-purple-50 to-purple-100',
    borderColor: 'border-purple-300',
    iconColor: 'text-purple-600'
  },
  'innovafast': {
    name: 'Dra. Sofia Fast',
    title: 'Mentora de Agilidad',
    quote: 'La velocidad con criterio es la clave de la innovación.',
    specialty: 'Experta en MVP y validación rápida',
    avatarIcon: Lightbulb,
    color: 'from-green-50 to-green-100',
    borderColor: 'border-green-300',
    iconColor: 'text-green-600'
  },
  'innovatech': {
    name: 'Dr. Alex Innovation',
    title: 'Mentor de Innovación',
    quote: 'Guío tu proceso experimental con precisión, criterio y datos.',
    specialty: 'Especialista en Lean Startup y BML',
    avatarImage: '/kuro.png', // Avatar real
    color: 'from-green-50 to-green-100',
    borderColor: 'border-green-300',
    iconColor: 'text-green-600'
  },
  'transforma360': {
    name: 'Prof. Carlos Transform',
    title: 'Mentor de Transformación',
    quote: 'Evoluciona tu modelo de negocio con visión estratégica.',
    specialty: 'Especialista en Business Model Canvas',
    avatarIcon: BarChart,
    color: 'from-green-50 to-green-100',
    borderColor: 'border-green-300',
    iconColor: 'text-green-600'
  },
  'dss_builder': {
    name: 'Prof. Data Mind',
    title: 'Mentor de Decisiones',
    quote: 'Los datos bien interpretados guían las mejores decisiones.',
    specialty: 'PhD en Data Science y Analytics',
    avatarIcon: Settings,
    color: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-300',
    iconColor: 'text-blue-600'
  },
  'analisis_riesgo': {
    name: 'Dra. Rita Risk',
    title: 'Mentora de Riesgos',
    quote: 'Anticipar riesgos es prevenir crisis.',
    specialty: 'Experta en modelado de escenarios',
    avatarImage: '/artemis.png', // Avatar real
    color: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-300',
    iconColor: 'text-blue-600'
  },
  'planificacion_estrategica': {
    name: 'Mtro. Pablo Strategy',
    title: 'Mentor de Estrategia',
    quote: 'Planificar con IA es diseñar el futuro con inteligencia.',
    specialty: 'Consultor en planificación organizacional',
    avatarIcon: Map,
    color: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-300',
    iconColor: 'text-blue-600'
  }
};

// Opciones de filtros
const CATEGORIES = [
  'Innovación y Emprendimiento',
  'Desarrollo de Producto',
  'Estrategia y Planificación',
  'Marketing y Ventas',
  'Finanzas y Métricas',
  'Tecnología y Desarrollo',
  'Liderazgo y Gestión',
  'Otro'
];

const DIFFICULTIES = [
  'Principiante',
  'Intermedio',
  'Avanzado'
];

const EplPublicLabs = () => {
  const { systemCard } = useParams(); // Obtener systemCard desde URL
  const navigate = useNavigate();
  const location = useLocation(); // ← Agregar para detectar navegación
  const { user } = useAuth(); // Obtener usuario autenticado
  const { labs, loading, error, actions } = useEplLabs();

  // Estado local para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Estado para completaciones del usuario
  const [userCompletions, setUserCompletions] = useState([]);
  const [loadingCompletions, setLoadingCompletions] = useState(false);

  // Validar systemCard y obtener nombre del sistema
  const systemName = SYSTEM_NAMES[systemCard] || 'Todos los Sistemas';
  const isValidSystem = Object.keys(SYSTEM_NAMES).includes(systemCard);

  // Cargar laboratorios y completaciones al montar componente
  // ✅ Se vuelve a cargar cada vez que cambia systemCard (al navegar entre tarjetas)
  // ✅ También se recarga al volver a la página desde cualquier ruta
  useEffect(() => {
    console.log('🔄 Recargando labs y completaciones para systemCard:', systemCard);
    console.log('🔄 location.key:', location.key, '- timestamp:', Date.now());
    // Cargar todos los labs (sin filtro de status, se filtrará en cliente)
    actions.fetchLabs();
    fetchUserCompletions();
  }, [systemCard, location.key]); // ← location.key cambia en cada navegación

  // Función para obtener completaciones del usuario
  const fetchUserCompletions = async () => {
    try {
      setLoadingCompletions(true);
      const token = getSafeToken();
      
      if (!token) {
        setLoadingCompletions(false);
        return;
      }

      // Bypass caché con timestamp en URL
      const endpoint = `${API_URL}/api/epl/user/completions?_t=${Date.now()}`;
      
      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const completions = response.data.data;
        
        // Obtener scores y attempts de cada sesión usando el stateId
        const enrichedCompletions = await Promise.all(
          completions.map(async (comp) => {
            try {
              const sessionEndpoint = `${API_URL}/api/epl/sessions/${comp.stateId}?_t=${Date.now()}`;
              
              const stateResponse = await axios.get(sessionEndpoint, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (stateResponse.data.success) {
                const session = stateResponse.data.data;
                const score = session.aiResults?.score || session.progress?.aiResults?.score || null;
                const attempts = session.progress?.aiAttempts || null;
                
                return {
                  ...comp,
                  score: score,
                  aiAttempts: attempts
                };
              }
            } catch (err) {
              console.warn(`⚠️ No se pudo obtener score para ${comp.labId}:`, err.message);
            }
            
            return comp;
          })
        );
        
        // 🔥 Limpiar estado anterior y forzar actualización
        setUserCompletions([]);
        setTimeout(() => {
          setUserCompletions(enrichedCompletions);
          console.log('📋 Completaciones enriquecidas actualizadas:', enrichedCompletions.length);
          console.log('🎯 Labs marcados como completados:', enrichedCompletions.map(c => `${c.labId} (score: ${c.score})`));
        }, 0);
      } else {
        console.error('❌ Response NO exitosa:', response.data);
        setCompletionsError('Response no exitosa: ' + JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error cargando completaciones:', error);
    } finally {
      setLoadingCompletions(false);
    }
  };

  // Función helper para verificar si un lab está completado
  const getLabCompletion = (lab) => {
    // Normalizar IDs a string para comparación confiable
    const labIdString = typeof lab._id === 'object' ? lab._id.toString() : String(lab._id);
    
    const completion = userCompletions.find(comp => {
      const compLabId = String(comp.labId);
      return compLabId === labIdString;
    });
    
    return completion;
  };

  // Filtrar laboratorios
  const filteredLabs = labs.filter(lab => {
    // Filtrar por status (activos y deshabilitados visibles, otros no)
    const allowedStatuses = ['active', 'disabled'];
    if (!allowedStatuses.includes(lab.status)) return false;

    // Filtrar por sistemas EPL asignados al usuario
    if (user && user.eplSystemsAssigned && user.eplSystemsAssigned.length > 0) {
      const allowedCards = user.eplSystemsAssigned.flatMap(
        system => EPL_SYSTEM_MAPPING[system] || []
      );
      
      if (allowedCards.length > 0 && !allowedCards.includes(lab.systemCard)) {
        console.log(`🔒 Lab "${lab.name}" filtrado - systemCard: ${lab.systemCard}, permitidas: ${allowedCards.join(', ')}`);
        return false;
      }
    }

    // Filtrar por systemCard si está especificado en URL
    if (systemCard && isValidSystem && lab.systemCard !== systemCard) {
      return false;
    }

    // Filtrar por búsqueda (título o descripción)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchTitle = lab.name?.toLowerCase().includes(search);
      const matchDesc = lab.description?.toLowerCase().includes(search);
      if (!matchTitle && !matchDesc) return false;
    }

    // Filtrar por categoría
    if (selectedCategory && lab.category !== selectedCategory) {
      return false;
    }

    // Filtrar por dificultad
    if (selectedDifficulty && lab.metadata?.difficulty !== selectedDifficulty) {
      return false;
    }

    return true;
  });

  // Manejar click en laboratorio
  const handleLabClick = (lab) => {
    // Si el lab está deshabilitado, no permitir ejecutarlo
    if (lab.status === 'disabled') {
      console.log('⚠️ Lab deshabilitado - No se puede ejecutar');
      return; // No hace nada
    }

    const completion = getLabCompletion(lab);
    
    console.log('🖱️ Click en lab:', lab.name);
    console.log('📊 Completion data:', completion);
    
    if (completion && completion.stateId) {
      // Si está completado, ir directo a resultados usando stateId
      console.log('✅ Lab completado - Navegando a resultados con stateId:', completion.stateId);
      navigate(`/epl/lab/${lab._id}?stateId=${completion.stateId}&view=results`);
    } else {
      // Si no está completado, ir al runner normal
      console.log('🆕 Lab no completado - Navegando a runner normal');
      navigate(`/epl/lab/${lab._id}`);
    }
  };

  // Renderizar estado de carga
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando laboratorios...</p>
        </div>
      </div>
    );
  }

  // Renderizar error
  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4">
            <p className="font-semibold">Error al cargar laboratorios</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
          <button
            onClick={() => actions.refresh()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Laboratorios EPL
              </h1>
              <p className="text-gray-600 mt-1">
                {isValidSystem
                  ? `Sistema: ${systemName}`
                  : 'Todos los sistemas disponibles'}
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>

          {/* Barra de búsqueda */}
          <div className="mt-6 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por título o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Panel de filtros expandible */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas las categorías</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dificultad
                </label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas las dificultades</option>
                  {DIFFICULTIES.map(diff => (
                    <option key={diff} value={diff}>{diff}</option>
                  ))}
                </select>
              </div>

              {/* Botón limpiar filtros */}
              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                    setSelectedDifficulty('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista de laboratorios */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        {filteredLabs.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No se encontraron laboratorios
            </h3>
            <p className="text-gray-600">
              {searchTerm || selectedCategory || selectedDifficulty
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'No hay laboratorios activos disponibles en este momento'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                {filteredLabs.length} laboratorio{filteredLabs.length !== 1 ? 's' : ''} encontrado{filteredLabs.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Tarjeta de Mentor - Primera posición del grid */}
              {systemCard && MENTORS_CONFIG[systemCard] && (() => {
                const mentorConfig = MENTORS_CONFIG[systemCard];
                const MentorIcon = mentorConfig.avatarIcon;
                const hasImage = !!mentorConfig.avatarImage;
                
                return (
                  <div className={`bg-gradient-to-br ${mentorConfig.color} rounded-lg shadow-md border-2 ${mentorConfig.borderColor} overflow-hidden`}>
                    <div className="p-4 md:p-6 h-full flex flex-col">
                      {/* Título superior */}
                      <div className="text-center mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Tu Coach IA
                        </p>
                      </div>

                      {/* Avatar - Imagen real o Ícono SVG */}
                      <div className="text-center mb-3 md:mb-4">
                        <div className="w-20 md:w-24 h-20 md:h-24 mx-auto mb-2 md:mb-3 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                          {hasImage ? (
                            <img 
                              src={mentorConfig.avatarImage} 
                              alt={mentorConfig.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <MentorIcon className={`w-12 md:w-14 h-12 md:h-14 ${mentorConfig.iconColor}`} />
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {mentorConfig.name}
                        </h3>
                      <p className="text-sm text-gray-600 font-medium">
                        {mentorConfig.title}
                      </p>
                    </div>

                    {/* Frase motivacional */}
                    <div className="mb-auto px-2 hidden md:block">
                      <p className="text-sm text-gray-700 italic text-center leading-relaxed">
                        "{mentorConfig.quote}"
                      </p>
                    </div>

                    {/* Especialidad */}
                    <div className="pt-4 mt-4 border-t border-gray-200">
                      <div className="flex items-start gap-2">
                        <GraduationCap className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-1">Especialidad</p>
                          <p className="text-sm font-medium text-gray-900">
                            {mentorConfig.specialty}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* Laboratorios */}
              {filteredLabs.map(lab => {
                const isDisabled = lab.status === 'disabled';
                const completion = getLabCompletion(lab);
                
                // Debug: mostrar solo labs completados
                // (logging reducido para producción)
                
                return (
                  <div
                    key={lab._id}
                    onClick={() => handleLabClick(lab)}
                    className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-200 ${
                      isDisabled 
                        ? 'opacity-75 cursor-not-allowed' 
                        : 'cursor-pointer'
                    }`}
                  >
                    {/* Header del card */}
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <h3 className="text-lg font-bold text-gray-900 line-clamp-2 flex-1">
                          {lab.name}
                        </h3>
                        {/* Badge de estado del lab - Esquina superior derecha */}
                        <div className="flex-shrink-0">
                          {completion ? (
                            <div className="flex items-center gap-1 px-2 py-1 bg-white border border-green-500 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Completado
                            </div>
                          ) : (
                            <EplStatusBadge status={lab.status} />
                          )}
                        </div>
                      </div>

                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {lab.description}
                    </p>

                    {/* Metadata */}
                    <div className="space-y-2">
                      {/* Dificultad */}
                      {lab.metadata?.difficulty && (
                        <div className="flex items-center gap-2 text-sm">
                          <Target className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{lab.metadata.difficulty}</span>
                        </div>
                      )}

                      {/* Duración estimada */}
                      {lab.metadata?.estimatedTime && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{lab.metadata.estimatedTime} min</span>
                        </div>
                      )}

                      {/* Categoría */}
                      {lab.category && (
                        <div className="flex items-center gap-2 text-sm">
                          <Tag className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700 line-clamp-1">{lab.category}</span>
                        </div>
                      )}

                      {/* Sistema */}
                      {lab.systemCard && (
                        <div className="flex items-center gap-2 text-sm">
                          <BookOpen className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">
                            {SYSTEM_NAMES[lab.systemCard] || lab.systemCard}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Información de completación */}
                    {completion && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-3 text-sm">
                          <Award className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          {completion.score !== null && completion.score !== undefined ? (
                            <>
                              <span className="font-bold text-base">
                                <span className={
                                  completion.score >= 80 ? 'text-green-600' :
                                  completion.score >= 60 ? 'text-amber-600' :
                                  'text-red-600'
                                }>
                                  {completion.score}
                                </span>
                                <span className="text-gray-500 font-normal text-sm">/100</span>
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-600">
                                {completion.aiAttempts || '--'} {completion.aiAttempts === 1 ? 'intento' : 'intentos'}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">Puntuación pendiente</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {lab.tags && lab.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {lab.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {lab.tags.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{lab.tags.length - 3} más
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer del card */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <button
                      disabled={isDisabled}
                      className={`w-full px-4 py-2 font-semibold rounded-lg transition-colors ${
                        isDisabled
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : completion
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {isDisabled 
                        ? 'No disponible' 
                        : completion 
                          ? 'Ver Resultados' 
                          : 'Comenzar laboratorio'
                      }
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EplPublicLabs;
